/*
 * Per-browser choices for agent alerts outside the app: desktop
 * notifications (on once permission is granted unless turned off), the chime
 * (off by default) and whether the enable prompt has been answered. Kept in
 * local storage: they describe this browser, not the account.
 */
import { useSyncExternalStore } from "react";

const KEYS = {
  desktop: "AGENT_ALERTS_DESKTOP_v1",
  sound: "AGENT_ALERTS_SOUND_v1",
  prompted: "AGENT_ALERTS_PROMPTED_v1"
} as const;
const SESSION_TURN = "AGENT_ALERTS_TURN_SENT";
const EVENT = "agent-alert-prefs";

export type AlertPrefs = {
  desktop: boolean;
  sound: boolean;
  prompted: boolean;
  /** An agent turn was sent in this browser session. */
  turnSent: boolean;
  permission: NotificationPermission | "unsupported";
};

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const write = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Private mode: the choice lasts for this page only.
  }
  window.dispatchEvent(new Event(EVENT));
};

export const notificationPermission = (): AlertPrefs["permission"] =>
  typeof window !== "undefined" && "Notification" in window
    ? Notification.permission
    : "unsupported";

let snapshot: AlertPrefs | null = null;
const compute = (): AlertPrefs => {
  let turnSent = false;
  try {
    turnSent = sessionStorage.getItem(SESSION_TURN) === "1";
  } catch {
    turnSent = false;
  }
  return {
    desktop: read(KEYS.desktop) !== "0",
    sound: read(KEYS.sound) === "1",
    prompted: read(KEYS.prompted) === "1",
    turnSent,
    permission: notificationPermission()
  };
};

export const getAlertPrefs = (): AlertPrefs => {
  const next = compute();
  if (snapshot && JSON.stringify(snapshot) === JSON.stringify(next))
    return snapshot;
  snapshot = next;
  return next;
};

const subscribe = (cb: () => void) => {
  window.addEventListener(EVENT, cb);
  window.addEventListener("storage", cb);
  return () => {
    window.removeEventListener(EVENT, cb);
    window.removeEventListener("storage", cb);
  };
};

export const useAlertPrefs = (): AlertPrefs =>
  useSyncExternalStore(subscribe, getAlertPrefs, getAlertPrefs);

export const setDesktopAlerts = (on: boolean) =>
  write(KEYS.desktop, on ? "1" : "0");
export const setAlertSound = (on: boolean) => write(KEYS.sound, on ? "1" : "0");
export const markAlertsPrompted = () => write(KEYS.prompted, "1");

export const noteAgentTurnSent = () => {
  try {
    if (sessionStorage.getItem(SESSION_TURN) === "1") return;
    sessionStorage.setItem(SESSION_TURN, "1");
  } catch {
    return;
  }
  window.dispatchEvent(new Event(EVENT));
};

/** Ask for desktop notification permission; resolves to the result. */
export const requestDesktopAlerts = async (): Promise<
  AlertPrefs["permission"]
> => {
  if (notificationPermission() === "unsupported") return "unsupported";
  const result = await Notification.requestPermission();
  if (result === "granted") setDesktopAlerts(true);
  window.dispatchEvent(new Event(EVENT));
  return result;
};
